import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { MachineryCard } from "@/components/MachineryCard";
import { MapView } from "@/components/MapView";
import api from "@/lib/api";
import { ArrowRight, Truck, ShieldCheck, FileText, MapPin, Wrench, Mountain } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1523848309072-c199db53f137?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";
const FEATURE = "https://images.unsplash.com/photo-1759745125627-333e78bc1edc?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";
const TRUST = "https://images.unsplash.com/photo-1681505531034-8d67054e07f6?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

const CATEGORIES = [
  { key: "excavadora", label: "Excavadoras", icon: Wrench },
  { key: "camion_minero", label: "Camiones Mineros", icon: Truck },
  { key: "cargador", label: "Cargadores", icon: Truck },
  { key: "grua", label: "Grúas", icon: Mountain },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [all, setAll] = useState([]);

  useEffect(() => {
    api.get("/machinery", { params: { featured: true } }).then((r) => setFeatured(r.data.slice(0, 3)));
    api.get("/machinery").then((r) => setAll(r.data));
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      {/* HERO */}
      <section className="relative min-h-[90vh] flex items-end overflow-hidden grain">
        <img src={HERO} alt="Maquinaria minera" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/20" />
        <div className="relative max-w-[1400px] mx-auto px-4 sm:px-6 pb-16 md:pb-24 w-full">
          <div className="max-w-3xl animate-fade-up">
            <div className="overline text-brand mb-4">Minería · Construcción · Movimiento de tierras</div>
            <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.9] text-white mb-6">
              MAQUINARIA PESADA<br /><span className="text-brand">LISTA PARA TRABAJAR</span>
            </h1>
            <p className="text-base md:text-lg text-white/70 max-w-xl mb-8">
              Alquiler y venta de equipos certificados con geolocalización en tiempo real, contratos digitales y soporte técnico 24/7.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/catalogo" data-testid="hero-catalog-btn"
                className="group flex items-center gap-2 px-8 py-4 bg-brand text-black font-semibold hover:bg-brand-hover transition-colors">
                Explorar catálogo <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link to="/mapa" data-testid="hero-map-btn"
                className="flex items-center gap-2 px-8 py-4 border border-white/30 text-white font-semibold hover:border-brand hover:text-brand transition-colors">
                <MapPin size={18} /> Ver mapa de equipos
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* STATS BAR */}
      <section className="bg-[#0A0A0A] text-white border-y border-white/10">
        <div className="max-w-[1400px] mx-auto grid grid-cols-2 md:grid-cols-4 divide-x divide-white/10">
          {[["+200", "Equipos disponibles"], ["24/7", "Soporte técnico"], ["15", "Años de experiencia"], ["100%", "Equipos certificados"]].map(([n, l]) => (
            <div key={l} className="px-6 py-10 text-center">
              <div className="font-display font-black text-4xl md:text-5xl text-brand tracking-tight">{n}</div>
              <div className="text-xs text-white/50 mt-2 uppercase tracking-wider">{l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="max-w-[1400px] mx-auto px-4 sm:px-6 py-20">
        <div className="overline text-brand mb-2">Categorías</div>
        <h2 className="font-display font-black text-4xl tracking-tight mb-10">EQUIPOS POR TIPO</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {CATEGORIES.map(({ key, label, icon: Icon }) => (
            <Link key={key} to={`/catalogo?category=${key}`} data-testid={`category-${key}`}
              className="group p-8 border border-border hover:border-brand hover:bg-brand/5 transition-colors">
              <Icon size={32} className="text-brand mb-4" />
              <div className="font-display font-bold text-2xl tracking-tight group-hover:text-brand transition-colors">{label}</div>
              <div className="text-sm text-muted-foreground mt-1 flex items-center gap-1">Ver equipos <ArrowRight size={14} /></div>
            </Link>
          ))}
        </div>
      </section>

      {/* FEATURED */}
      <section className="max-w-[1400px] mx-auto px-4 sm:px-6 pb-20">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="overline text-brand mb-2">Destacados</div>
            <h2 className="font-display font-black text-4xl tracking-tight">EQUIPOS DESTACADOS</h2>
          </div>
          <Link to="/catalogo" className="hidden md:flex items-center gap-2 text-sm font-semibold hover:text-brand transition-colors">
            Ver todo <ArrowRight size={16} />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {featured.map((m) => <MachineryCard key={m.id} m={m} />)}
        </div>
      </section>

      {/* B2B / CONTRACTS */}
      <section className="bg-secondary">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-20 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="overline text-brand mb-3">Contratos digitales</div>
            <h2 className="font-display font-black text-4xl md:text-5xl tracking-tight leading-none mb-6">
              CONTRATOS DE ALQUILER Y VENTA EN PDF
            </h2>
            <p className="text-muted-foreground mb-8">
              Genera y descarga contratos profesionales al instante. Gestión completa de tus operaciones desde tu panel privado.
            </p>
            <div className="space-y-4">
              {[
                [FileText, "Contratos PDF automáticos", "Documentos legales generados con un clic."],
                [ShieldCheck, "Equipos certificados", "Cumplimiento de normas de seguridad minera."],
                [MapPin, "Geolocalización", "Ubica cada equipo en el mapa en tiempo real."],
              ].map(([Icon, t, d]) => (
                <div key={t} className="flex gap-4">
                  <div className="h-10 w-10 shrink-0 bg-brand flex items-center justify-center"><Icon size={18} className="text-black" /></div>
                  <div><div className="font-semibold">{t}</div><div className="text-sm text-muted-foreground">{d}</div></div>
                </div>
              ))}
            </div>
            <Link to="/contratos" data-testid="home-contracts-btn"
              className="inline-flex items-center gap-2 mt-8 px-8 py-4 bg-foreground text-background font-semibold hover:bg-brand hover:text-black transition-colors">
              Conocer más <ArrowRight size={18} />
            </Link>
          </div>
          <div className="relative aspect-[4/3]">
            <img src={TRUST} alt="Contratos B2B" className="h-full w-full object-cover border border-border" />
            <div className="absolute -bottom-4 -left-4 bg-brand text-black p-6 max-w-[220px] hidden md:block">
              <div className="font-display font-black text-3xl tracking-tight">+500</div>
              <div className="text-xs font-semibold uppercase tracking-wider">Contratos firmados con clientes del sector</div>
            </div>
          </div>
        </div>
      </section>

      {/* MAP */}
      <section className="max-w-[1400px] mx-auto px-4 sm:px-6 py-20">
        <div className="overline text-brand mb-2">Cobertura</div>
        <h2 className="font-display font-black text-4xl tracking-tight mb-8">UBICACIÓN DE NUESTROS EQUIPOS</h2>
        <MapView items={all} height="480px" />
      </section>

      <Footer />
    </div>
  );
}
